import {
  Component,
  OnInit,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { AppRoutes } from '@core/navigation';
import { M3ButtonComponent } from '@shared/button/m3-button.component';
import {
  DataTableComponent,
  DataTableColumn,
  ActionCellDefDirective,
  CallStatus,
  SelectionChangeEvent,
} from '@shared/data-table';
import { PaginationComponent } from '@shared/pagination';
import { TextBlockCategoryStore } from './state/text-block-category.store';
import { TextBlockCategory } from './models/text-block-category.model';
import { TextBlockCategoriesDragGridComponent } from './components/text-block-categories-drag-grid.component';

@Component({
  selector: 'app-text-block-categories-list',
  standalone: true,
  imports: [
    M3ButtonComponent,
    DataTableComponent,
    ActionCellDefDirective,
    PaginationComponent,
    TextBlockCategoriesDragGridComponent,
  ],
  template: `
    <div class="text-block-categories-container">
      <header class="page-header">
        <h2 class="mat-title-large">Textkategorien</h2>
        <p class="page-description">
          Verwaltung und Bearbeitung von Textkategorien ({{
            store.totalCount()
          }}
          Kategorien, {{ store.totalTextBlocks() }} Textbausteine)
        </p>
      </header>

      <div class="actions-bar">
        <div class="actions-left">
          <app-m3-button
            variant="filled"
            iconLeft="add"
            (clicked)="onCreateCategory()"
          >
            Neue Kategorie erstellen
          </app-m3-button>

          <app-m3-button
            variant="tonal"
            iconLeft="delete"
            [disabled]="store.textBlockCategoryIdsSelected().length === 0"
            (clicked)="onDeleteSelected()"
          >
            Ausgewählte löschen ({{
              store.textBlockCategoryIdsSelected().length
            }})
          </app-m3-button>

          <app-m3-button
            [variant]="reorderMode() ? 'filled' : 'outlined'"
            [iconLeft]="reorderMode() ? 'done' : 'swap_vert'"
            (clicked)="onToggleReorder()"
          >
            {{ reorderMode() ? 'Fertig' : 'Sortieren' }}
          </app-m3-button>
        </div>
      </div>

      <div class="content-wrapper">
        @if (reorderMode()) {
          <div class="drag-grid-wrapper">
            <app-text-block-categories-drag-grid
              [categories]="categoriesToDisplay()"
              (reorderCategories)="onReorderCategories($event)"
            />
          </div>
        } @else {
          <div class="table-container">
            <app-data-table
              [data]="paginatedCategories()"
              [columns]="columns"
              [callStatus]="callStatus()"
              selectionMode="multi"
              [selectViaColumnOnly]="true"
              [showSelectAll]="true"
              [selectedIds]="selectedIdsSet()"
              [ariaLabel]="'Textkategorien'"
              [emptyMessage]="'Keine Textkategorien vorhanden.'"
              [rowLabelAccessor]="'name'"
              (rowActivated)="onEditCategory($event)"
              (selectionChange)="onSelectionChange($event)"
            >
              <ng-template
                appActionCellDef
                let-row
                (actionClick)="onDeleteCategory($event.row)"
              >
                <span
                  class="material-symbols-outlined"
                  [class.disabled]="row.textBlockCount > 0"
                  aria-hidden="true"
                >
                  delete
                </span>
              </ng-template>
            </app-data-table>
          </div>

          <app-pagination
            [currentPage]="
              store.textBlockCategoryEntitiesPagination().currentPage + 1
            "
            [totalItems]="
              store.textBlockCategoryEntitiesCurrentPage().total ?? 0
            "
            [itemsPerPage]="
              store.textBlockCategoryEntitiesPagination().pageSize
            "
            (pageChange)="onPageChange($event)"
          />
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .text-block-categories-container {
        height: 100%;
        display: flex;
        flex-direction: column;
      }

      .page-header {
        padding: 24px 24px 0 24px;
        margin-bottom: 24px;
        flex-shrink: 0;
      }

      h2 {
        margin: 0 0 8px 0;
        color: var(--md-sys-color-on-surface);
      }

      .page-description {
        font-size: 14px;
        line-height: 20px;
        color: var(--md-sys-color-on-surface-variant);
        margin: 0;
      }

      .actions-bar {
        padding: 0 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;
        flex-shrink: 0;
      }

      .actions-left {
        display: flex;
        gap: 12px;
      }

      .actions-left app-m3-button {
        margin-right: 12px;
      }

      .actions-left app-m3-button:last-child {
        margin-right: 0;
      }

      .content-wrapper {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .table-container {
        flex: 1;
        min-height: 0;
        overflow-x: auto;
        padding: 0 24px;
      }

      .drag-grid-wrapper {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }

      .drag-grid-wrapper app-text-block-categories-drag-grid {
        display: block;
        flex: 1;
        min-height: 0;
      }

      .content-wrapper app-pagination {
        flex-shrink: 0;
        border-top: 1px solid var(--md-sys-color-outline-variant);
      }

      .material-symbols-outlined.disabled {
        opacity: 0.38;
        cursor: not-allowed;
      }

      @media (max-width: 600px) {
        .page-header {
          padding: 16px 16px 0 16px;
        }

        .actions-bar {
          padding: 0 16px;
          flex-direction: column;
          align-items: stretch;
          gap: 16px;
        }

        .actions-left {
          flex-direction: column;
          gap: 8px;
        }

        .actions-left app-m3-button {
          margin-right: 0;
          width: 100%;
        }
      }
    `,
  ],
})
export class TextBlockCategoriesListComponent implements OnInit {
  protected readonly store = inject(TextBlockCategoryStore);
  private readonly router = inject(Router);

  protected readonly reorderMode = signal<boolean>(false);
  private readonly localCategories = signal<TextBlockCategory[]>([]);

  protected readonly columns: DataTableColumn<TextBlockCategory>[] = [
    { id: 'name', label: 'Name', accessor: 'name', highlight: true },
    {
      id: 'textBlockCount',
      label: 'Anzahl Textbausteine',
      accessor: 'textBlockCount',
    },
    { id: 'updatedAt', label: 'Geändert', accessor: 'updatedAt', type: 'date' },
  ];

  protected readonly categoriesToDisplay = computed(() =>
    this.localCategories().length
      ? this.localCategories()
      : this.store.textBlockCategoryEntities()
  );

  protected readonly paginatedCategories = computed(() => {
    if (this.localCategories().length) {
      return this.localCategories();
    }
    return (this.store.textBlockCategoryEntitiesCurrentPage().entities ??
      []) as TextBlockCategory[];
  });

  protected readonly selectedIdsSet = computed(
    () =>
      new Set(this.store.textBlockCategoryIdsSelected().map((id) => Number(id)))
  );

  protected readonly callStatus = computed<CallStatus>(() => {
    if (this.store.isTextBlockCategoryEntitiesLoading()) {
      return 'loading';
    }
    return 'loaded';
  });

  ngOnInit(): void {
    this.store.setTextBlockCategoryEntitiesLoading();
  }

  onCreateCategory(): void {
    this.router.navigate(AppRoutes.textBlockCategoriesCreate);
  }

  onEditCategory(category: TextBlockCategory): void {
    this.router.navigate(AppRoutes.textBlockCategoriesEdit(category.id));
  }

  onDeleteCategory(category: TextBlockCategory): void {
    if (category.textBlockCount > 0) {
      this.store.toastService.warning(
        'Kategorie kann nicht gelöscht werden',
        `Diese Kategorie enthält ${category.textBlockCount} Textbausteine und kann nicht gelöscht werden.`
      );
      return;
    }
    this.store.requestDeleteDialog(category.id);
  }

  onDeleteSelected(): void {
    this.store.requestDeleteSelectedDialog();
  }

  onPageChange(page: number): void {
    this.store.loadTextBlockCategoryEntitiesPage({ pageIndex: page - 1 });
  }

  onSelectionChange(event: SelectionChangeEvent<TextBlockCategory>): void {
    if (event.source === 'selectAll') {
      this.onToggleAll();
    } else if (event.item) {
      this.onToggleSelection(event.item.id);
    }
  }

  onToggleSelection(categoryId: number): void {
    const selected = this.store.textBlockCategoryIdsSelected();
    if (selected.includes(categoryId)) {
      this.store.deselectTextBlockCategoryEntities({ id: categoryId });
    } else {
      this.store.selectTextBlockCategoryEntities({ id: categoryId });
    }
  }

  onToggleAll(): void {
    const categories = this.store.textBlockCategoryEntities();
    const selected = this.store.textBlockCategoryIdsSelected();

    if (selected.length === categories.length) {
      const allIds = categories.map((category) => category.id);
      this.store.deselectTextBlockCategoryEntities({ ids: allIds });
    } else {
      const allIds = categories.map((category) => category.id);
      this.store.selectTextBlockCategoryEntities({ ids: allIds });
    }
  }

  onToggleReorder(): void {
    const next = !this.reorderMode();

    if (next) {
      this.localCategories.set([...this.store.textBlockCategoryEntities()]);
    } else {
      const finalOrder = this.localCategories();
      this.store.updateCategoryPositions(finalOrder);
    }

    this.reorderMode.set(next);
  }

  onReorderCategories(reorderedCategories: TextBlockCategory[]): void {
    this.localCategories.set(reorderedCategories);
  }
}
